import { formatDateRange } from '@/shared/lib/time-utils';

import { SESSION_GENERATE_ACTION } from '../config/subscription.config';
import type { SubscriptionUsageHistory } from '../model/subscription.types';

export type UsageHistoryDisplayItem = {
    periodLabel: string;
    sessions: number;
    /** Cycle session limit; null = unlimited cycle. */
    included: number | null;
};

export type UsageHistoryDisplayData = {
    /** Newest billing cycle first. */
    items: UsageHistoryDisplayItem[];
    /** NSS-470 1a/1b: hidden during the first billing cycle, visible from the second. */
    showCard: boolean;
    /** NSS-470 1c: the time filter only appears from the 7th billing cycle onward. */
    showTimeFilter: boolean;
};

const SHOW_CARD_FROM_CYCLES = 2;
const SHOW_FILTER_FROM_CYCLES = 7;

export function usageHistoryToDisplay(history: SubscriptionUsageHistory): UsageHistoryDisplayData {
    const items = history.periods
        .filter((period) => period.periodStart !== null && period.periodEnd !== null)
        .map((period) => ({
            periodLabel: formatPeriodLabel(
                new Date(period.periodStart as string),
                new Date(period.periodEnd as string),
            ),
            sessions: period.counters[SESSION_GENERATE_ACTION] ?? 0,
            included: period.limits[SESSION_GENERATE_ACTION] ?? null,
        }));

    // Gated on cycles we actually hold, not account age: a cycle with no usage leaves no
    // aggregate item, so there is nothing to chart for it either way.
    return {
        items,
        showCard: items.length >= SHOW_CARD_FROM_CYCLES,
        showTimeFilter: items.length >= SHOW_FILTER_FROM_CYCLES,
    };
}

const DAY_MS = 86_400_000;

// Stripe's periodEnd is exclusive, so the label ends a day earlier. UTC because these are
// date-only boundaries — the viewer's zone would shift every label west of Greenwich.
function formatPeriodLabel(periodStart: Date, periodEnd: Date): string {
    return formatDateRange(periodStart, new Date(periodEnd.getTime() - DAY_MS), {
        timeZone: 'UTC',
    });
}
